import { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Eye,
  MousePointer,
  ShoppingCart,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  LucideIcon,
  DollarSign,
  ExternalLink,
  Building2,
  ArrowLeft,
  X,
  Phone,
  CalendarCheck,
  FileText,
  Gift,
  UtensilsCrossed,
  BarChart3,
  Search,
} from 'lucide-react';
import type { RestaurantSemData, Campaign, AdGroup, ConversionAction, KeywordData } from '@/types/sem';
import { normalizeRestaurantName, normalizeAdGroupName, normalizeCampaignName } from '@/lib/normalize-names';

interface RestaurantCampaignCardsProps {
  restaurants: RestaurantSemData[];
  onViewLandingPage?: (restaurantName?: string) => void;
}

// ============================================
// Formatting Utilities
// ============================================

function formatCurrency(value: number, compact = false): string {
  if (compact && value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

// ============================================
// Grade System
// ============================================

type Grade = 'good' | 'ok' | 'bad';

const gradeColors: Record<Grade, { text: string; bar: string; bg: string }> = {
  good: { text: 'text-[var(--ed-green)]', bar: 'bg-[var(--ed-green)]', bg: 'bg-[var(--ed-green-bg)]' },
  ok: { text: 'text-[var(--ed-amber)]', bar: 'bg-[var(--ed-amber)]', bg: 'bg-[var(--ed-amber-bg)]' },
  bad: { text: 'text-[var(--ed-red)]', bar: 'bg-[var(--ed-red)]', bg: 'bg-[var(--ed-red-bg)]' },
};

function getGrade(value: number, thresholds: { good: number; ok: number }): Grade {
  if (value >= thresholds.good) return 'good';
  if (value >= thresholds.ok) return 'ok';
  return 'bad';
}

// ============================================
// Visual Funnel Components (preserved from original)
// ============================================

type FunnelStageNumber = 1 | 2 | 3 | 4;

interface FunnelStageProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  secondaryLabel: string;
  secondaryValue: string;
  stage: FunnelStageNumber;
  secondaryGrade?: Grade;
}

function FunnelStage({
  icon: Icon,
  label,
  value,
  secondaryLabel,
  secondaryValue,
  stage,
  secondaryGrade,
}: FunnelStageProps) {
  const opacity = [0.03, 0.05, 0.07, 0.1][stage - 1];

  return (
    <div className="flex-1 min-w-0">
      <div
        className="relative h-full rounded-2xl overflow-hidden"
        style={{
          background: `linear-gradient(135deg, rgba(255, 255, 255, ${opacity * 0.6}) 0%, rgba(255, 255, 255, ${opacity * 0.2}) 100%)`,
        }}
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
        <div className="relative p-5 h-full flex flex-col">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Icon className="h-4 w-4 text-[var(--ed-t3)]" strokeWidth={1.5} />
            <span className="text-xs font-medium tracking-wide text-[var(--ed-t3)] uppercase">{label}</span>
          </div>
          <p className="text-3xl font-display font-light tabular-nums text-center tracking-tight text-foreground">{value}</p>
          <div className="mt-auto pt-4">
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="text-[11px] text-[var(--ed-t4)] uppercase tracking-wider">{secondaryLabel}</span>
              <span className={`text-sm font-medium tabular-nums ${secondaryGrade ? gradeColors[secondaryGrade].text : 'text-[var(--ed-t2)]'}`}>
                {secondaryValue}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FunnelArrowProps {
  rate: number;
  label: string;
  grade: Grade;
  showWarning?: boolean;
}

function FunnelArrow({ rate, label, grade, showWarning = false }: FunnelArrowProps) {
  const colors = gradeColors[grade];
  return (
    <div className="flex flex-col items-center justify-center shrink-0 w-16 px-1">
      <span className={`text-sm font-semibold tabular-nums ${colors.text}`}>{formatPercent(rate)}</span>
      <div className="relative w-full my-2">
        <div className={`h-0.5 ${colors.bar} rounded-full opacity-60`} />
        <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[5px] ${grade === 'good' ? 'border-l-[var(--ed-green)]' : grade === 'ok' ? 'border-l-[var(--ed-amber)]' : 'border-l-[var(--ed-red)]'}`} />
      </div>
      <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">{label}</span>
      {showWarning && <AlertTriangle className="h-3.5 w-3.5 text-[var(--ed-amber)]/80 mt-1" />}
    </div>
  );
}

function FunnelConnector() {
  return (
    <div className="flex flex-col items-center justify-center w-12 shrink-0 px-1">
      <div className="relative w-full">
        <div className="h-0.5 bg-white/[0.08] rounded-full" />
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[5px] border-l-white/[0.12]" />
      </div>
    </div>
  );
}

// ============================================
// Match Type Badge
// ============================================

const matchTypeStyles: Record<string, { label: string; className: string }> = {
  exact: { label: 'Exact', className: 'bg-[var(--ed-green-bg)] text-[var(--ed-green)] border-[rgba(82,224,148,0.15)]' },
  phrase: { label: 'Phrase', className: 'bg-[rgba(96,165,250,0.08)] text-[rgba(96,165,250,0.9)] border-[rgba(96,165,250,0.15)]' },
  broad: { label: 'Broad', className: 'bg-[var(--ed-amber-bg)] text-[var(--ed-amber)] border-[rgba(224,168,82,0.15)]' },
};

function MatchTypeBadge({ type }: { type: string }) {
  const style = matchTypeStyles[type] || matchTypeStyles.broad;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider border ${style.className}`}>
      {style.label}
    </span>
  );
}

// ============================================
// Keyword Breakdown (Top 5 + Expandable Full List)
// ============================================

function KeywordBreakdown({ keywords }: { keywords: KeywordData[] }) {
  const [expanded, setExpanded] = useState(false);

  if (!keywords || keywords.length === 0) return null;

  // Sort by spend DESC (highest cost first = most impactful)
  const sorted = [...keywords]
    .filter(k => k.metrics.clicks > 0)
    .sort((a, b) => b.metrics.spend - a.metrics.spend);

  if (sorted.length === 0) return null;

  const top3 = sorted.slice(0, 3);
  const remaining = sorted.slice(3);
  const displayList = expanded ? sorted : top3;

  return (
    <div className="mt-5 pt-4 border-t border-white/[0.04]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground/60" />
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Top Keywords</h4>
        </div>
        <span className="text-[10px] text-muted-foreground/40 tabular-nums">{sorted.length} with clicks</span>
      </div>

      {/* Table header */}
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-4 px-1 pb-2 border-b border-white/[0.06]">
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider">Keyword</span>
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider text-center w-14">Match</span>
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider text-right w-14">Clicks</span>
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider text-right w-14">CPC</span>
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider text-right w-16">Spend</span>
      </div>

      {/* Rows */}
      <div className="space-y-0.5">
        {displayList.map((kw, i) => (
          <div
            key={`${kw.keyword}-${kw.matchType}`}
            className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-4 items-center px-1 py-2 rounded-lg hover:bg-white/[0.02] transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] text-muted-foreground/30 tabular-nums w-4 shrink-0 text-right">{i + 1}</span>
              <span className="text-xs font-medium truncate">{kw.keyword}</span>
            </div>
            <div className="w-14 flex justify-center">
              <MatchTypeBadge type={kw.matchType} />
            </div>
            <span className="text-xs tabular-nums font-medium text-right w-14">{formatNumber(kw.metrics.clicks)}</span>
            <span className="text-xs tabular-nums text-muted-foreground text-right w-14">${kw.metrics.cpc.toFixed(2)}</span>
            <span className="text-xs tabular-nums font-medium text-right w-16">{formatCurrency(kw.metrics.spend)}</span>
          </div>
        ))}
      </div>

      {/* Expand toggle */}
      {remaining.length > 0 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-muted-foreground/60 hover:text-muted-foreground hover:bg-white/[0.02] transition-colors"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? 'Show less' : `+${remaining.length} more keywords`}
        </button>
      )}
    </div>
  );
}

// ============================================
// Conversion Summary (Sleek top-2 + expandable, above funnel)
// ============================================

function ConversionSummary({ actions }: { actions: ConversionAction[] }) {
  const [expanded, setExpanded] = useState(false);

  if (!actions || actions.length === 0) return null;

  const filtered = actions.filter(a => a.conversions > 0);
  if (filtered.length === 0) return null;

  // Sort by conversions DESC
  const sorted = [...filtered].sort((a, b) => b.conversions - a.conversions);
  const top1 = sorted.slice(0, 1);
  const remaining = sorted.slice(1);
  const displayList = expanded ? sorted : top1;

  const totalConv = filtered.reduce((s, a) => s + a.conversions, 0);
  const totalValue = filtered.reduce((s, a) => s + a.conversionValue, 0);

  return (
    <div className="px-5 pb-4">
      <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] overflow-hidden">
        {/* Header row */}
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-white/[0.04]">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5 text-muted-foreground/50" />
            <span className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">Conversions</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs tabular-nums text-muted-foreground/50">{totalConv.toFixed(0)} total</span>
            <span className="text-xs tabular-nums font-semibold text-[var(--ed-green)]">{formatCurrency(totalValue)}</span>
          </div>
        </div>

        {/* Conversion rows */}
        <div className="divide-y divide-white/[0.03]">
          {displayList.map((action) => {
            const Icon = getConversionIcon(action.actionName);
            const color = getConversionColor(action.actionName);
            const displayName = shortenActionName(action.actionName);
            return (
              <div key={action.actionName} className="px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.015] transition-colors">
                <div className="p-1 rounded-md shrink-0" style={{ background: `${color}12` }}>
                  <Icon className="h-3.5 w-3.5" style={{ color }} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-medium truncate flex-1 min-w-0">{displayName}</span>
                <div className="flex items-center gap-5 shrink-0">
                  <div className="text-right">
                    <span className="text-xs font-semibold tabular-nums">{action.conversions.toFixed(0)}</span>
                  </div>
                  <div className="text-right w-16">
                    <span className="text-xs tabular-nums text-[var(--ed-green)]">{formatCurrency(action.conversionValue)}</span>
                  </div>
                  <div className="text-right w-16">
                    <span className="text-[10px] tabular-nums text-muted-foreground/50">{formatCurrency(action.valuePerEvent)}/ea</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Expand toggle */}
        {remaining.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full px-4 py-2 flex items-center justify-center gap-1.5 border-t border-white/[0.04] text-[11px] font-medium text-muted-foreground/50 hover:text-muted-foreground hover:bg-white/[0.015] transition-colors"
          >
            <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            {expanded ? 'Show less' : `+${remaining.length} more`}
          </button>
        )}

        {/* View-through footnote */}
        {actions.some(a => a.viewThroughConversions > 0) && (
          <div className="px-4 py-1.5 border-t border-white/[0.04]">
            <p className="text-[10px] text-muted-foreground/30 tabular-nums">
              + {actions.reduce((s, a) => s + a.viewThroughConversions, 0).toFixed(0)} view-through
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Ad Group Funnel (Full Visual)
// ============================================

// CPA threshold - anything over $60 is concerning
const HIGH_CPA_THRESHOLD = 60;

function AdGroupFunnel({ adGroup, keywords, onViewLandingPage, restaurantName }: { adGroup: AdGroup; keywords?: KeywordData[]; onViewLandingPage?: (restaurantName?: string) => void; restaurantName?: string }) {
  const { metrics } = adGroup;

  const roas = metrics.spend > 0 ? metrics.conversionValue / metrics.spend : 0;
  const ctr = metrics.ctr;
  const conversionRate = metrics.clicks > 0 ? (metrics.conversions / metrics.clicks) * 100 : 0;
  const cpm = metrics.impressions > 0 ? (metrics.spend / metrics.impressions) * 1000 : 0;
  const cpa = metrics.cpa || (metrics.conversions > 0 ? metrics.spend / metrics.conversions : 0);

  const ctrGrade = getGrade(ctr, { good: 3, ok: 1.5 });
  const convRateGrade = getGrade(conversionRate, { good: 5, ok: 2 });
  const roasGrade = getGrade(roas, { good: 4, ok: 2 });
  // CPA grading: under $30 is good, under $60 is ok, over $60 is bad
  const cpaGrade: Grade = cpa > 0 ? (cpa <= 30 ? 'good' : cpa <= HIGH_CPA_THRESHOLD ? 'ok' : 'bad') : 'ok';

  const hasLandingPageProblem = ctrGrade !== 'bad' && convRateGrade === 'bad';
  const hasHighCpa = cpa > HIGH_CPA_THRESHOLD && metrics.conversions > 0;

  return (
    <div className="ed-card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`h-2 w-2 rounded-full ${adGroup.status === 'enabled' ? 'bg-[var(--ed-green)]' : 'bg-muted-foreground/50'}`} />
          <span className="font-medium tracking-tight">{normalizeAdGroupName(adGroup.name)}</span>
          {hasHighCpa && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--ed-red-bg)] text-[var(--ed-red)] border border-[rgba(224,90,82,0.15)]">
              <DollarSign className="h-3 w-3" />
              <span className="text-[10px] font-medium uppercase tracking-wide">High CPA</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* CPA Badge - Key Metric */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${hasHighCpa ? 'bg-[var(--ed-red-bg)] border border-[rgba(224,90,82,0.2)]' : 'bg-[var(--ed-bg-2)]'}`}>
            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">CPA</span>
            <span className={`text-sm font-semibold tabular-nums ${gradeColors[cpaGrade].text}`}>
              {cpa > 0 ? formatCurrency(cpa) : '-'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[var(--ed-bg-2)]">
            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">ROAS</span>
            <span className={`text-sm font-semibold tabular-nums ${gradeColors[roasGrade].text}`}>{roas.toFixed(2)}x</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[var(--ed-bg-2)]">
            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">QS</span>
            <span className={`text-sm font-semibold tabular-nums ${Math.round(metrics.qualityScore) >= 7 ? 'text-[var(--ed-green)]' : Math.round(metrics.qualityScore) >= 5 ? 'text-[var(--ed-amber)]' : 'text-[var(--ed-red)]'}`}>
              {Math.round(metrics.qualityScore)}
            </span>
          </div>
        </div>
      </div>

      {/* Conversion Events — above funnel */}
      {adGroup.conversionActions && adGroup.conversionActions.length > 0 && (
        <ConversionSummary actions={adGroup.conversionActions} />
      )}

      {/* Visual Funnel */}
      <div className="px-5 pb-5">
        <div className="flex items-stretch gap-2">
          <FunnelStage icon={Eye} label="Impressions" value={formatNumber(metrics.impressions)} secondaryLabel="CPM" secondaryValue={`$${cpm.toFixed(2)}`} stage={1} />
          <FunnelArrow rate={ctr} label="CTR" grade={ctrGrade} />
          <FunnelStage icon={MousePointer} label="Clicks" value={formatNumber(metrics.clicks)} secondaryLabel="CPC" secondaryValue={`$${metrics.cpc.toFixed(2)}`} stage={2} />
          <FunnelArrow rate={conversionRate} label="Conv Rate" grade={convRateGrade} showWarning={hasLandingPageProblem} />
          <FunnelStage icon={ShoppingCart} label="Conversions" value={Math.round(metrics.conversions)} secondaryLabel="CPA" secondaryValue={formatCurrency(cpa)} stage={3} secondaryGrade={cpaGrade} />
          <FunnelConnector />
          <FunnelStage icon={DollarSign} label="Revenue" value={formatCurrency(metrics.conversionValue)} secondaryLabel="ROAS" secondaryValue={`${roas.toFixed(2)}x`} stage={4} secondaryGrade={roasGrade} />
        </div>

        {/* Diagnosis Banner */}
        {hasHighCpa && hasLandingPageProblem ? (
          <div className="mt-5 rounded-xl bg-[var(--ed-red-bg)] p-4 flex items-start gap-3 border border-[rgba(224,90,82,0.15)]">
            <div className="p-1.5 rounded-lg bg-[var(--ed-red-bg)]">
              <DollarSign className="h-4 w-4 text-[var(--ed-red)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--ed-red)]">High CPA — Landing Page Bottleneck</p>
              <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">
                <span className="text-[var(--ed-red)] font-semibold">{formatCurrency(cpa)} CPA</span> is above the $60 threshold.
                Your ads work — <span className="text-foreground/80">{formatPercent(ctr)} CTR</span> proves people click.
                But only <span className="text-foreground/80">{formatPercent(conversionRate)}</span> convert,
                meaning <span className="text-[var(--ed-red)]">the landing page is losing potential customers</span>.
              </p>
              {onViewLandingPage && (
                <button onClick={() => onViewLandingPage?.(restaurantName)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--ed-red)] hover:text-[var(--ed-red)] transition-colors">
                  Analyze Landing Page Issues
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ) : hasLandingPageProblem ? (
          <div className="mt-5 rounded-xl bg-[var(--ed-amber-bg)] p-4 flex items-start gap-3">
            <div className="p-1.5 rounded-lg bg-[var(--ed-amber-bg)]">
              <AlertTriangle className="h-4 w-4 text-[var(--ed-amber)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--ed-amber)]">Landing Page Issue Detected</p>
              <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">
                Ads are performing well — <span className="text-foreground/80">{formatPercent(ctr)} CTR</span>. But only{' '}
                <span className="text-foreground/80">{formatPercent(conversionRate)}</span> convert on the landing page.
              </p>
              {onViewLandingPage && (
                <button onClick={() => onViewLandingPage?.(restaurantName)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--ed-amber)] hover:text-[var(--ed-amber)] transition-colors">
                  View Landing Page Analysis
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ) : hasHighCpa ? (
          <div className="mt-5 rounded-xl bg-[var(--ed-red-bg)] p-4 flex items-start gap-3 border border-[rgba(224,90,82,0.15)]">
            <div className="p-1.5 rounded-lg bg-[var(--ed-red-bg)]">
              <DollarSign className="h-4 w-4 text-[var(--ed-red)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--ed-red)]">High CPA Alert</p>
              <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">
                <span className="text-[var(--ed-red)] font-semibold">{formatCurrency(cpa)} CPA</span> is above the $60 threshold.
                Review landing page conversion optimization.
              </p>
              {onViewLandingPage && (
                <button onClick={() => onViewLandingPage?.(restaurantName)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--ed-red)] hover:text-[var(--ed-red)] transition-colors">
                  Analyze Landing Page
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ) : roasGrade === 'good' ? (
          <div className="mt-5 rounded-xl bg-[var(--ed-green-bg)] p-4 flex items-start gap-3">
            <div className="p-1.5 rounded-lg bg-[var(--ed-green-bg)]">
              <CheckCircle2 className="h-4 w-4 text-[var(--ed-green)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--ed-green)]">Strong Performance</p>
              <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">
                Generating <span className="text-foreground/80">{formatCurrency(metrics.conversionValue)}</span> from{' '}
                <span className="text-foreground/80">{formatCurrency(metrics.spend)}</span> spend —{' '}
                <span className="text-[var(--ed-green)]">{roas.toFixed(2)}x return</span>.
              </p>
            </div>
          </div>
        ) : ctrGrade === 'bad' ? (
          <div className="mt-5 rounded-xl bg-[var(--ed-red-bg)] p-4 flex items-start gap-3">
            <div className="p-1.5 rounded-lg bg-[var(--ed-red-bg)]">
              <XCircle className="h-4 w-4 text-[var(--ed-red)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--ed-red)]">Low Click-Through Rate</p>
              <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">
                Only <span className="text-foreground/80">{formatPercent(ctr)}</span> of impressions convert to clicks. Consider revising ad copy.
              </p>
            </div>
          </div>
        ) : null}

        {/* Spend vs Revenue Summary */}
        <div className="mt-5 pt-4 border-t border-white/[0.04] flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Spent</span>
              <span className="text-sm font-medium tabular-nums">{formatCurrency(metrics.spend)}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Generated</span>
              <span className="text-sm font-semibold tabular-nums">{formatCurrency(metrics.conversionValue)}</span>
            </div>
          </div>
          <div className={`text-sm font-semibold tabular-nums ${metrics.conversionValue > metrics.spend ? 'text-[var(--ed-green)]' : 'text-[var(--ed-red)]'}`}>
            {metrics.conversionValue > metrics.spend ? '+' : ''}
            {formatCurrency(metrics.conversionValue - metrics.spend)}
          </div>
        </div>

        {/* Keyword Breakdown */}
        {keywords && keywords.length > 0 && (
          <KeywordBreakdown keywords={keywords} />
        )}
      </div>
    </div>
  );
}

// ============================================
// Conversion Action Icon Mapping
// ============================================

function getConversionIcon(actionName: string): LucideIcon {
  const lower = actionName.toLowerCase();
  if (lower.includes('call')) return Phone;
  if (lower.includes('reservation') || lower.includes('opentable') || lower.includes('tablz') || lower.includes('booking')) return CalendarCheck;
  if (lower.includes('form')) return FileText;
  if (lower.includes('gift')) return Gift;
  if (lower.includes('toast') || lower.includes('order')) return UtensilsCrossed;
  return ShoppingCart;
}

function getConversionColor(actionName: string): string {
  const lower = actionName.toLowerCase();
  if (lower.includes('call')) return 'var(--ed-blue, #60a5fa)';
  if (lower.includes('reservation') || lower.includes('opentable') || lower.includes('tablz') || lower.includes('booking')) return 'var(--ed-green)';
  if (lower.includes('form')) return 'var(--ed-amber)';
  if (lower.includes('gift')) return '#c084fc';
  if (lower.includes('toast') || lower.includes('order')) return '#f97316';
  return 'var(--ed-t2)';
}

function shortenActionName(name: string): string {
  return name
    .replace(/\s*---\s*\d+\/\d+\/\d+/g, '')       // Remove date suffixes
    .replace(/\s*\(3rd\s*[Pp]arty\)/g, '')         // Remove "(3rd party)"
    .replace(/\s*-\s*\d+\s*[Ss]ec\s*CL/g, '')     // Remove "- 120 Sec CL"
    .replace(/Button Click/gi, '')                  // Shorten "Button Click"
    .replace(/\s+/g, ' ')
    .trim();
}

// ============================================
// Conversion Events Breakdown
// ============================================

function ConversionBreakdown({ actions, compact = false }: { actions: ConversionAction[]; compact?: boolean }) {
  if (!actions || actions.length === 0) return null;

  const totalConv = actions.reduce((s, a) => s + a.conversions, 0);
  const totalValue = actions.reduce((s, a) => s + a.conversionValue, 0);
  const filtered = actions.filter(a => a.conversions > 0);

  if (filtered.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground/60" />
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Conversion Events</h4>
        </div>
        <div className="flex items-baseline gap-3">
          <span className="text-xs text-muted-foreground/50">{totalConv.toFixed(0)} total</span>
          <span className="text-xs font-medium text-[var(--ed-green)]">{formatCurrency(totalValue)}</span>
        </div>
      </div>

      {/* Table header */}
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 px-1 pb-1 border-b border-white/[0.06]">
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider">Action</span>
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider text-right">Count</span>
        <span className="text-[10px] text-muted-foreground/40 uppercase tracking-wider text-right w-20">Value</span>
      </div>

      {/* Rows */}
      <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
        {filtered.map((action) => {
          const Icon = getConversionIcon(action.actionName);
          const color = getConversionColor(action.actionName);
          const displayName = shortenActionName(action.actionName);

          return (
            <div key={action.actionName} className="grid grid-cols-[1fr_auto_auto] gap-x-6 items-center px-1 py-1.5 rounded-lg hover:bg-white/[0.02] transition-colors">
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="p-1 rounded-md shrink-0"
                  style={{ background: `${color}15` }}
                >
                  <Icon className="h-3.5 w-3.5" style={{ color }} strokeWidth={1.5} />
                </div>
                <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium truncate`}>{displayName}</span>
              </div>
              <span className={`${compact ? 'text-xs' : 'text-sm'} tabular-nums font-semibold text-right`}>{action.conversions.toFixed(0)}</span>
              <span className={`${compact ? 'text-xs' : 'text-sm'} tabular-nums text-[var(--ed-green)] text-right w-20`}>{formatCurrency(action.conversionValue)}</span>
            </div>
          );
        })}
      </div>

      {actions.some(a => a.viewThroughConversions > 0) && (
        <div className="pt-2 border-t border-white/[0.04]">
          <p className="text-[10px] text-muted-foreground/40 uppercase tracking-wider">
            + {actions.reduce((s, a) => s + a.viewThroughConversions, 0).toFixed(0)} view-through conversions
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// Restaurant Detail Panel (Slide-out)
// ============================================

function RestaurantDetailPanel({
  restaurant,
  onClose,
  onViewLandingPage,
}: {
  restaurant: RestaurantSemData;
  onClose: () => void;
  onViewLandingPage?: (restaurantName?: string) => void;
}) {
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const { data } = restaurant;
  const { summary } = data;

  // Filter out campaigns and ad groups with $0 spend
  const activeCampaigns = data.campaigns
    .filter(c => c.metrics.spend > 0)
    .map(c => ({
      ...c,
      adGroups: c.adGroups.filter(ag => ag.metrics.spend > 0)
    }))
    .filter(c => c.adGroups.length > 0);

  const displayName = normalizeRestaurantName(restaurant.name);
  const roasGrade = getGrade(summary.roas, { good: 4, ok: 2 });

  // Lock body scroll when panel is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[1000] flex h-screen">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel - 80% width, full viewport height with safe bottom padding */}
      <div className="relative ml-auto w-full sm:w-[85%] md:w-[80%] h-screen bg-background border-l border-white/[0.04] shadow-2xl overflow-auto pb-20">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-background border-b border-white/[0.04]">
          <div className="px-6 py-4 flex items-center justify-between">
            <button onClick={onClose} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back to Overview</span>
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--ed-bg-3)] transition-colors">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          <div className="px-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-[var(--ed-bg-3)] border border-[var(--ed-rule)]">
                <Building2 className="h-6 w-6 text-[var(--ed-t2)]" />
              </div>
              <div>
                <h2 className="text-2xl font-display font-light tracking-tight">{displayName}</h2>
                <p className="text-sm text-muted-foreground">
                  {activeCampaigns.length} campaign{activeCampaigns.length !== 1 ? 's' : ''} • {activeCampaigns.reduce((s, c) => s + c.adGroups.length, 0)} ad group{activeCampaigns.reduce((s, c) => s + c.adGroups.length, 0) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Top-line Metrics */}
          <div className="px-6 pb-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            <div className="rounded-xl ed-inset p-3">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Spend</p>
              <p className="text-xl font-light tabular-nums mt-1">{formatCurrency(summary.spend)}</p>
            </div>
            <div className="rounded-xl ed-inset p-3">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Revenue</p>
              <p className="text-xl font-light tabular-nums text-[var(--ed-green)] mt-1">{formatCurrency(summary.conversionValue)}</p>
            </div>
            <div className="rounded-xl ed-inset p-3">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">ROAS</p>
              <p className={`text-xl font-semibold tabular-nums mt-1 ${gradeColors[roasGrade].text}`}>{summary.roas.toFixed(2)}x</p>
            </div>
            <div className="rounded-xl ed-inset p-3">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">CTR</p>
              <p className="text-xl font-light tabular-nums mt-1">{summary.ctr.toFixed(2)}%</p>
            </div>
            <div className="rounded-xl ed-inset p-3">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Conversions</p>
              <p className="text-xl font-light tabular-nums mt-1">{formatNumber(summary.conversions)}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Conversion Events Breakdown */}
          {restaurant.conversionActions && restaurant.conversionActions.length > 0 && !selectedCampaign && (
            <div className="ed-card p-5">
              <ConversionBreakdown actions={restaurant.conversionActions} />
            </div>
          )}

          {!selectedCampaign ? (
            // Campaign List
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Campaigns</h3>
              <div className="space-y-2">
                {activeCampaigns.map((campaign) => {
                  const hasLpIssue = campaign.adGroups.some((ag) => {
                    const ctr = ag.metrics.ctr;
                    const convRate = ag.metrics.clicks > 0 ? (ag.metrics.conversions / ag.metrics.clicks) * 100 : 0;
                    return ctr >= 1.5 && convRate < 2 && ag.metrics.clicks > 50;
                  });
                  const campRoasGrade = getGrade(campaign.metrics.roas, { good: 4, ok: 2 });

                  return (
                    <button
                      key={campaign.id}
                      onClick={() => setSelectedCampaign(campaign)}
                      className={`w-full px-5 py-4 rounded-xl border text-left flex items-center justify-between hover:bg-[var(--ed-bg-2)] transition-colors ${
                        hasLpIssue ? 'border-[rgba(224,168,82,0.12)] bg-[var(--ed-amber-bg)]' : 'border-white/[0.04]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`h-2 w-2 rounded-full ${campaign.status === 'enabled' ? 'bg-[var(--ed-green)]' : 'bg-muted-foreground/40'}`} />
                        <div>
                          <p className="font-medium">{normalizeCampaignName(campaign.name)}</p>
                          <p className="text-xs text-muted-foreground/60 mt-0.5">{campaign.adGroups.length} ad group{campaign.adGroups.length !== 1 ? 's' : ''}</p>
                        </div>
                        {hasLpIssue && <AlertTriangle className="h-4 w-4 text-[var(--ed-amber)]" />}
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground/50">Spend</p>
                          <p className="text-sm font-medium tabular-nums">{formatCurrency(campaign.metrics.spend, true)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground/50">Revenue</p>
                          <p className="text-sm font-medium tabular-nums text-[var(--ed-green)]">{formatCurrency(campaign.metrics.conversionValue, true)}</p>
                        </div>
                        <div className="text-right w-16">
                          <p className="text-xs text-muted-foreground/50">ROAS</p>
                          <p className={`text-sm font-semibold tabular-nums ${gradeColors[campRoasGrade].text}`}>{campaign.metrics.roas.toFixed(2)}x</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground/40" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            // Campaign Detail with Ad Group Funnels
            <div className="space-y-6">
              <button
                onClick={() => setSelectedCampaign(null)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Campaigns
              </button>

              <div>
                <h3 className="text-xl font-medium">{normalizeCampaignName(selectedCampaign.name)}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {formatCurrency(selectedCampaign.metrics.spend)} spend • {formatCurrency(selectedCampaign.metrics.conversionValue)} revenue •{' '}
                  <span className={gradeColors[getGrade(selectedCampaign.metrics.roas, { good: 4, ok: 2 })].text}>
                    {selectedCampaign.metrics.roas.toFixed(2)}x ROAS
                  </span>
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Ad Group Performance ({selectedCampaign.adGroups.length})
                </h4>
                {selectedCampaign.adGroups.map((ag) => {
                  const agKeywords = data.keywords.filter(
                    k => k.adGroupName === ag.name && k.campaignName === selectedCampaign.name
                  );
                  return (
                    <AdGroupFunnel
                      key={ag.id}
                      adGroup={ag}
                      keywords={agKeywords}
                      onViewLandingPage={onViewLandingPage}
                      restaurantName={displayName}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Restaurant Row (Overview List)
// ============================================

function RestaurantRow({
  restaurant,
  onClick,
}: {
  restaurant: RestaurantSemData;
  onClick: () => void;
}) {
  const { data } = restaurant;
  const { summary } = data;

  // Filter out campaigns and ad groups with $0 spend for display
  const activeCampaigns = data.campaigns
    .filter(c => c.metrics.spend > 0)
    .map(c => ({
      ...c,
      adGroups: c.adGroups.filter(ag => ag.metrics.spend > 0)
    }))
    .filter(c => c.adGroups.length > 0);

  const activeAdGroupCount = activeCampaigns.reduce((s, c) => s + c.adGroups.length, 0);

  const hasLandingPageIssues = activeCampaigns.some((c) =>
    c.adGroups.some((ag) => {
      const ctr = ag.metrics.ctr;
      const convRate = ag.metrics.clicks > 0 ? (ag.metrics.conversions / ag.metrics.clicks) * 100 : 0;
      return ctr >= 1.5 && convRate < 2 && ag.metrics.clicks > 50;
    })
  );

  const roasGrade = getGrade(summary.roas, { good: 4, ok: 2 });
  const displayName = normalizeRestaurantName(restaurant.name);
  const isProfitable = summary.conversionValue > summary.spend;

  // Don't show restaurants with no active campaigns
  if (activeCampaigns.length === 0) return null;

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-5 hover:bg-[var(--ed-bg-2)] -mx-4 px-4 rounded-lg transition-colors group text-left"
    >
      <div className="flex items-center gap-4">
        {/* Status Icon */}
        <div className={`p-2 rounded-xl ${hasLandingPageIssues ? 'bg-[var(--ed-amber-bg)]' : isProfitable ? 'bg-[var(--ed-green-bg)]' : 'bg-[var(--ed-red-bg)]'}`}>
          {hasLandingPageIssues ? (
            <AlertTriangle className="h-5 w-5 text-[var(--ed-amber)]" />
          ) : isProfitable ? (
            <CheckCircle2 className="h-5 w-5 text-[var(--ed-green)]" />
          ) : (
            <XCircle className="h-5 w-5 text-[var(--ed-red)]" />
          )}
        </div>

        <div>
          <h3 className="font-medium group-hover:text-white transition-colors">{displayName}</h3>
          <p className="text-sm text-muted-foreground">
            {activeCampaigns.length} campaign{activeCampaigns.length !== 1 ? 's' : ''} • {activeAdGroupCount} ad group{activeAdGroupCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Spend</p>
          <p className="font-medium tabular-nums">{formatCurrency(summary.spend, true)}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Revenue</p>
          <p className="font-medium tabular-nums text-[var(--ed-green)]">{formatCurrency(summary.conversionValue, true)}</p>
        </div>
        <div className="text-right w-16">
          <p className="text-sm text-muted-foreground">ROAS</p>
          <p className={`font-semibold tabular-nums ${gradeColors[roasGrade].text}`}>{summary.roas.toFixed(2)}x</p>
        </div>
        <div className="text-right w-14">
          <p className="text-sm text-muted-foreground">CTR</p>
          <p className="font-medium tabular-nums text-sm">{summary.ctr.toFixed(1)}%</p>
        </div>
        <div className="text-right w-14">
          <p className="text-sm text-muted-foreground">CPC</p>
          <p className="font-medium tabular-nums text-sm">${summary.cpc.toFixed(2)}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-white transition-colors" />
      </div>
    </button>
  );
}

// ============================================
// Main Component
// ============================================

export function RestaurantCampaignCards({ restaurants, onViewLandingPage }: RestaurantCampaignCardsProps) {
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantSemData | null>(null);

  // Filter restaurants that have active campaigns (with spend > 0)
  const activeRestaurants = restaurants.filter(r => {
    const activeCampaigns = r.data.campaigns.filter(c => c.metrics.spend > 0);
    return activeCampaigns.length > 0;
  });

  const sorted = [...activeRestaurants].sort((a, b) => b.data.summary.roas - a.data.summary.roas);

  const totalSpend = activeRestaurants.reduce((sum, r) => sum + r.data.summary.spend, 0);
  const totalRevenue = activeRestaurants.reduce((sum, r) => sum + r.data.summary.conversionValue, 0);
  const totalConversions = activeRestaurants.reduce((sum, r) => sum + r.data.summary.conversions, 0);
  const overallRoas = totalSpend > 0 ? totalRevenue / totalSpend : 0;

  return (
    <div className="space-y-12">
      {/* Key Metrics */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-8">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Total Spend</p>
          <p className="text-4xl font-display font-light tabular-nums">{formatCurrency(totalSpend)}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
          <p className="text-4xl font-display font-light tabular-nums text-[var(--ed-green)]">{formatCurrency(totalRevenue)}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Overall ROAS</p>
          <p className={`text-4xl font-display font-light tabular-nums ${gradeColors[getGrade(overallRoas, { good: 4, ok: 2 })].text}`}>
            {overallRoas.toFixed(2)}x
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Total Conversions</p>
          <p className="text-4xl font-display font-light tabular-nums">{formatNumber(totalConversions)}</p>
        </div>
      </section>

      {/* Restaurant List */}
      <section className="space-y-4">
        <h2 className="text-lg font-medium text-muted-foreground uppercase tracking-wider">All Restaurants</h2>
        <div className="divide-y divide-white/[0.04]">
          {sorted.map((restaurant) => (
            <RestaurantRow
              key={restaurant.id}
              restaurant={restaurant}
              onClick={() => setSelectedRestaurant(restaurant)}
            />
          ))}
        </div>
      </section>

      {/* Detail Panel */}
      {selectedRestaurant && (
        <RestaurantDetailPanel
          restaurant={selectedRestaurant}
          onClose={() => setSelectedRestaurant(null)}
          onViewLandingPage={onViewLandingPage}
        />
      )}
    </div>
  );
}
